import { select } from "@angular-redux/store";
import { Component, Input, OnInit } from "@angular/core";
import { Observable } from "rxjs/Observable";
import { ComponentSource, ModalType } from "../../models/component-source";
import { SharedActions } from "../../shared.actions";
import { SharedService } from "../../services/shared.service";
import { PLANNED, PRE_PLANNED, TO_BE_PLANNED } from "../../models/status-type.interface";
import { DepotInActions } from "../../../depot-in/depot-in.actions";
import { DepotOutActions } from "../../../depot-out/depot-out.actions";
import { ModalManagerService } from "../../services/modal-manager.service";
import { PlanningBlock } from "../../../pb-screen/models/planning-block/planning-block.interface";
import { MsalService } from "../../../msal";
import { UserFeedbackService } from "../../../services/user-feedback.service";
import { DepotOptimisticPlanningService } from "../../../optimistic-planning/depot-optimistic-planning.service";


@Component({
  selector: "unplan-modal-cpl",
  templateUrl: "./unplan-modal-CPL.component.html",
})

export class UnplanModalComponentCPL implements OnInit {
  @Input() public source: string;
  @select("depotOutSelection") public depotOutSelection$: Observable<PlanningBlock[]>;
  @select("depotInSelection") public depotInSelection$: Observable<PlanningBlock[]>;
  @select("loading") public loading$: Observable<boolean>;

  public modalType = ModalType;
  public selectedPlanningBlocks: PlanningBlock[] = [];
  public componentSource = ComponentSource;
  public loadingSpinner: boolean = false;
  isAllPlanned: boolean = false;
  isAllPrePlanned: boolean = false;

  constructor(
    private readonly sharedService: SharedService,
    private readonly sharedActions: SharedActions,
    private readonly userFeedbackService: UserFeedbackService,
    private readonly depotInActions: DepotInActions,
    private readonly depotOutActions: DepotOutActions,
    private readonly modalManager: ModalManagerService,
    private readonly authService: MsalService,
    private readonly depotOptimistic: DepotOptimisticPlanningService
  ) {
  }

  public ngOnInit(): void {
    this.loading$.subscribe((data) => {
      if (!data) {
        this.loadingSpinner = data;
      }
    });
    this.depotInSelection$.subscribe((depotInSelection: PlanningBlock[]) => {
      if (this.source === this.componentSource.depotIn) {
        this.selectedPlanningBlocks = depotInSelection;
        this.setButtonStatuses();
      }
    });
    this.depotOutSelection$.subscribe((depotOutSelection: PlanningBlock[]) => {
      if (this.source === this.componentSource.depotOut) {
        this.selectedPlanningBlocks = depotOutSelection;
        this.setButtonStatuses();
      }
    });

  }

  setButtonStatuses() {
    this.isAllPlanned = this.selectedPlanningBlocks.every((x) => x.status.statusType == PLANNED);
    this.isAllPrePlanned = this.selectedPlanningBlocks.every((x) => x.status.statusType == PRE_PLANNED);
  }

  /** Swallows repeat clicks while an unplan is in flight — a double-submit fires two concurrent
   *  BC write chains on the same TO and trips BC's stale-record error. Success closes the modal;
   *  the error path releases the flag. */
  public unplanInFlight = false;

  public unplanAction(cancelSendToHaulier: boolean): void {
    if (this.unplanInFlight) return;
    this.unplanInFlight = true;

    // Optimistic path: lock the affected rows, close the dialog instantly and let the
    // unplan run in the background with inline per-row feedback.
    if (this.depotOptimistic.isEnabled) {
      const planningBlocks = [...this.selectedPlanningBlocks];
      this.depotOptimistic.unplanOptimistic({ planningBlocks, cancelSendToHaulier, source: this.source });
      this.sharedActions.resetModalDomains();
      this.closeModal();
      this.depotInActions.setDepotInSelection([]);
      this.depotOutActions.setDepotOutSelection([]);
      return;
    }

    const pbIds: number[] = [];
    this.loadingSpinner = true;
    const account = this.authService.getAccount();
    this.selectedPlanningBlocks.map((pb: PlanningBlock) => pbIds.push(pb.id));

    this.sharedService.unplan(pbIds, account.username, cancelSendToHaulier).subscribe((unplannedPbs: PlanningBlock[]) => {
      // CASE : everything is successfull
      this.userFeedbackService.showSuccessMessage("TOASTR_MESSAGES.SUCCESS.UNPLAN_SUCCEEDED");
      this.sharedActions.updatePlanningBlocksDisplayCpl(unplannedPbs);
      this.loadingSpinner = false;
      this.sharedActions.resetModalDomains();
      this.closeModal();
      this.depotInActions.setDepotInSelection([]);
      this.depotOutActions.setDepotOutSelection([]);
    }, _ => {
      // CASE : error but still, some pbs may be successfully unplanned
      this.loadingSpinner = false;
      this.unplanInFlight = false;
      this.sharedActions.setUiState(true);
      this.checkPbList(pbIds);
    });

  }

  public checkPbList(pbIds: number[]): void {
    let failedPbs: PlanningBlock[] = [];
    this.sharedService.getPlanningBlocksByIds(pbIds).toPromise().then((res: PlanningBlock[]) => {
      res.forEach((pb: PlanningBlock) => {
        if (pb.status.statusType !== TO_BE_PLANNED) {
          failedPbs.push(pb);
        }
      });
    }).then(() => {
      if (this.source === this.componentSource.depotIn) {
        this.depotInActions.setDepotInSelection(failedPbs);
      } else if (this.source === this.componentSource.depotOut) {
        this.depotOutActions.setDepotOutSelection(failedPbs);
      }
    });
  }

  public closeModal(): void {
    this.modalManager.closeModal(this.modalType.unplan);
    this.sharedActions.setUiState(true);
  }
}
